import type { ChannelForm } from '../components/ChannelForm/ChannelForm.types';
import type { ChannelListItem } from '@/models';

const isBlank = (value: unknown): boolean =>
  value === undefined || value === null || (typeof value === 'string' && value.trim() === '');

const PERSISTED_LINE_ITEM_KEYS = [
  'channel',
  'product',
  'kpi',
  'formats',
  'buyingMetric',
  'sellRate',
  'budget',
  'rateSource',
  'packageId',
  'applyAgencyDiscount',
  'valueAddType',
  'valueAddInputMode',
  'valueAddPercent',
  'valueAddAbsolute',
  'feePercent',
  'feeBaseChannel',
  'country',
  'currency',
  'dsp',
  'targetingDetails',
  'targetingDevices',
  'poweredBySkyrise'
] as const;

/** Fields required for rate-card resolution on the backend. */
export function pickLineItemBaseline(original?: ChannelListItem): ChannelForm {
  if (!original) return {};

  const formats = normalizeFormatsField(original as ChannelForm);
  const product = normalizeProductField(original.product);

  return {
    channel: original.channel,
    product,
    kpi: original.kpi,
    ...(formats ? { formats } : {}),
    buyingMetric: original.buyingMetric,
    sellRate: original.sellRate,
    budget: original.budget,
    rateSource: original.rateSource ?? 'MANUAL',
    packageId: original.packageId,
    applyAgencyDiscount: original.applyAgencyDiscount !== false,
    valueAddType: original.valueAddType === 'ADDED_VALUE' ? 'ADDED_VALUE' : 'VALUE_POT',
    valueAddInputMode: original.valueAddInputMode === 'ABSOLUTE' ? 'ABSOLUTE' : 'PERCENT',
    valueAddPercent: original.valueAddPercent,
    valueAddAbsolute: original.valueAddAbsolute,
    feePercent: original.feePercent,
    feeBaseChannel: original.feeBaseChannel,
    country: original.country,
    currency: original.currency,
    dsp: original.dsp,
    targetingDetails: original.targetingDetails,
    targetingDevices: original.targetingDevices,
    poweredBySkyrise: original.poweredBySkyrise
  };
}

/** Later layers override earlier ones only when the value is non-blank. */
export function mergeLineItemLayers(...layers: Array<ChannelForm | undefined>): ChannelForm {
  const result: ChannelForm = {};

  for (const layer of layers) {
    if (!layer) continue;
    for (const key of PERSISTED_LINE_ITEM_KEYS) {
      const value = layer[key];
      if (!isBlank(value)) {
        (result as Record<string, unknown>)[key] = value;
      }
    }
    if (layer.flightDates) {
      result.flightDates = layer.flightDates;
    }
  }

  return result;
}

/** Merge wizard state with the original line item so edits never drop persisted fields. */
export function mergeChannelFormValues(
  formValues: ChannelForm,
  accumulated: ChannelForm,
  original?: ChannelListItem
): ChannelForm {
  return mergeLineItemLayers(pickLineItemBaseline(original), accumulated, formValues);
}

/**
 * Product as the single string the API stores.
 *
 * Typed as a string, but older lines and some option pickers hand back an
 * array of names, so the value is checked at runtime and arrays are joined.
 *
 * @param value - The raw `product` value from a line item or form.
 * @returns The product string, or `undefined` when there is none.
 */
export function normalizeProductField(value: unknown): string | undefined {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.filter(Boolean).join(', ');
  return undefined;
}

export function normalizeFormatsField(values: ChannelForm): string[] | undefined {
  const fromFormats = values.formats;
  if (Array.isArray(fromFormats) && fromFormats.length > 0) {
    return fromFormats.filter(Boolean);
  }
  const legacyFormat = (values as { format?: string | string[] }).format;
  if (Array.isArray(legacyFormat) && legacyFormat.length > 0) {
    return legacyFormat.filter(Boolean);
  }
  if (typeof legacyFormat === 'string' && legacyFormat.trim()) {
    return [legacyFormat.trim()];
  }
  return undefined;
}

export function withSelectOption<T extends string>(options: T[], current?: string): T[] {
  if (!current?.trim()) return options;
  if (options.includes(current as T)) return options;
  return [current as T, ...options];
}
